import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, IconButton, Stack, Button } from '@mui/material';
import CloseIcon       from '@mui/icons-material/Close';
import OpenInNewIcon   from '@mui/icons-material/OpenInNew';
import InfoIcon        from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon     from '@mui/icons-material/Warning';
import ErrorIcon       from '@mui/icons-material/Error';

import { AppDispatch, RootState } from '../reduxStore/Store';
import { dismissToast, markAsRead, ActiveToast } from '../reduxStore/NotificationSlice';
import { openLink } from '../utils/openLink';

/** Auto-fermeture après 30 min — protection anti-encombrement (un user revenant après plusieurs heures
 *  ne se retrouve pas avec 30 toasts à fermer). Le badge cloche reste, les notifs sont toujours
 *  consultables dans le drawer. */
const AUTO_CLOSE_MS = 30 * 60 * 1000;

/** Style par niveau : couleur d'accent appliquée uniquement au titre, à l'icône
 *  et à la barre latérale gauche. Le fond reste blanc pour lisibilité maximale. */
const NIVEAU_STYLE: Record<ActiveToast['niveau'], {
  color:   string;
  bgIcon:  string;
  Icon:    React.ComponentType<{ fontSize?: 'small' | 'inherit' | 'large' | 'medium' }>;
}> = {
  INFO:    { color: '#0288d1', bgIcon: '#e1f5fe', Icon: InfoIcon },
  SUCCESS: { color: '#2e7d32', bgIcon: '#e8f5e9', Icon: CheckCircleIcon },
  WARNING: { color: '#ed6c02', bgIcon: '#fff4e5', Icon: WarningIcon },
  ERROR:   { color: '#d32f2f', bgIcon: '#fdecea', Icon: ErrorIcon },
};

const NotificationToastStack: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const toasts   = useSelector((s: RootState) => s.NotificationSlice.activeToasts);

  // Auto-fermeture par toast après AUTO_CLOSE_MS — un timer par toast actif
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    toasts.forEach((t) => {
      const remaining = AUTO_CLOSE_MS - (Date.now() - t.arrived_at);
      if (remaining <= 0) {
        dispatch(dismissToast(t.notification_id));
        return;
      }
      timers.push(setTimeout(() => dispatch(dismissToast(t.notification_id)), remaining));
    });
    return () => { timers.forEach(clearTimeout); };
  }, [toasts, dispatch]);

  const handleClose = (id: number) => dispatch(dismissToast(id));

  const handleClickToast = (t: ActiveToast) => {
    // Click sur le toast → marque lu (le reducer markAsRead.fulfilled retire le toast)
    dispatch(markAsRead(t.notification_id));
  };

  const handleVoir = (e: React.MouseEvent, t: ActiveToast) => {
    e.stopPropagation();   // ne pas déclencher handleClickToast en plus
    if (t.lien) openLink(t.lien, navigate);
    dispatch(markAsRead(t.notification_id));
  };

  if (toasts.length === 0) return null;

  return (
    <Box
      sx={{
        position:      'fixed',
        top:           80,           // sous la Navbar (height ~64 + marge)
        right:         16,
        zIndex:        1400,         // au-dessus des dialogs MUI (1300) mais sous tooltips (1500)
        width:         { xs: 'calc(100vw - 32px)', sm: 380 },
        pointerEvents: 'none',       // les zones vides du Box laissent passer les clics
      }}
    >
      <Stack spacing={1.25}>
        {toasts.map((t) => {
          const style = NIVEAU_STYLE[t.niveau];
          const Icon  = style.Icon;
          return (
            <Paper
              key={t.notification_id}
              elevation={6}
              onClick={() => handleClickToast(t)}
              sx={{
                pointerEvents:  'auto',
                cursor:         'pointer',
                bgcolor:        '#ffffff',
                color:          'text.primary',
                borderLeft:     `4px solid ${style.color}`,
                borderRadius:   1.5,
                overflow:       'hidden',
                transition:     'transform 0.15s, box-shadow 0.15s',
                '&:hover':      { transform: 'translateY(-1px)', boxShadow: 8 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5 }}>
                {/* Icône colorée sur fond très pâle du même ton */}
                <Box
                  sx={{
                    width:          32,
                    height:         32,
                    borderRadius:   '50%',
                    bgcolor:        style.bgIcon,
                    color:          style.color,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    flexShrink:     0,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>

                {/* Contenu : titre coloré + message normal */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color:      style.color,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      mb:         0.25,
                    }}
                  >
                    {t.titre}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color:      'text.primary',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.4,
                    }}
                  >
                    {t.message}
                  </Typography>
                  {t.lien && (
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => handleVoir(e, t)}
                      sx={{
                        mt:            0.75,
                        px:            1,
                        py:            0.25,
                        minHeight:     0,
                        textTransform: 'none',
                        color:         style.color,
                        bgcolor:       style.bgIcon,
                        fontWeight:    600,
                        '&:hover':     { bgcolor: style.bgIcon, opacity: 0.85 },
                      }}
                    >
                      Voir
                    </Button>
                  )}
                </Box>

                {/* Bouton fermeture discret */}
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleClose(t.notification_id); }}
                  aria-label="Fermer la notification"
                  sx={{
                    color:     'text.secondary',
                    flexShrink: 0,
                    mt:        -0.5,
                    mr:        -0.5,
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default NotificationToastStack;
